
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { TrendingUp, TrendingDown, Minus, Users, AlertTriangle } from "lucide-react"
import { TeamDetailPage } from "./team-detail-page"
import { getOrgEqTeamAggregates } from "../../api/helpers"

interface EmotionAnalysis {
  emotion: string
  communication_evidence: string[]
  discussion_topics: string[]
  team_dynamics: string
}

interface TrajectorySummary {
  overall_summary: string
  top_emotions_analysis: EmotionAnalysis[]
  communication_channel_insights: {
    meeting_discussions: string
    email_communications: string
    team_chat_exchanges: string
  }
  leadership_action_items: string[]
}

interface TeamOverview {
  id: string
  name: string
  memberCount: number
  positivityScore: number // Added positivity score for each team
  recentEmotions: string[] // Changed from single dominantEmotion to array of recent emotions
  emotionIntensity: number
  trend: "up" | "down" | "stable"
  riskLevel: "improving" | "no change" | "at-risk"
  topTheme: string
  trajectorySummary?: TrajectorySummary | null
}

interface OrgOverviewProps {
  selectedOrganization?: string
  selectedTracker?: string
}

const mockTeams: TeamOverview[] = [
  {
    id: "1",
    name: "Engineering",
    memberCount: 12,
    positivityScore: 52,
    recentEmotions: ["Anxiety", "Tiredness", "Distress"],
    emotionIntensity: 0.8,
    trend: "down",
    riskLevel: "at-risk",
    topTheme: "Sprint Planning",
    trajectorySummary: null,
  },
  {
    id: "2",
    name: "Product",
    memberCount: 8,
    positivityScore: 85,
    recentEmotions: ["Excitement", "Pride", "Triumph"],
    emotionIntensity: 0.8,
    trend: "up",
    riskLevel: "improving",
    topTheme: "Feature Launch",
    trajectorySummary: null,
  },
  {
    id: "3",
    name: "Design",
    memberCount: 6,
    positivityScore: 72,
    recentEmotions: ["Frustration", "Contemplation", "Interest"],
    emotionIntensity: 0.6,
    trend: "stable",
    riskLevel: "no change",
    topTheme: "Design System",
    trajectorySummary: null,
  },
  {
    id: "4",
    name: "Marketing",
    memberCount: 10,
    positivityScore: 58,
    recentEmotions: ["Anxiety", "Distress", "Doubt"],
    emotionIntensity: 0.9,
    trend: "down",
    riskLevel: "at-risk",
    topTheme: "Campaign Deadline",
    trajectorySummary: null,
  },
  {
    id: "5",
    name: "Sales",
    memberCount: 15,
    positivityScore: 82,
    recentEmotions: ["Determination", "Excitement", "Pride"],
    emotionIntensity: 0.75,
    trend: "up",
    riskLevel: "improving",
    topTheme: "Q4 Targets",
    trajectorySummary: null,
  },
  {
    id: "6",
    name: "Support",
    memberCount: 7,
    positivityScore: 45,
    recentEmotions: ["Anger", "Distress", "Tiredness"],
    emotionIntensity: 0.88,
    trend: "down",
    riskLevel: "at-risk",
    topTheme: "Ticket Volume",
    trajectorySummary: null,
  },
]

const getEmotionColor = (emotion: string, intensity: number) => {
  // Hume emotion valence mapping
  const emotionValence: { [key: string]: number } = {
    Concentration: 0.3,
    Determination: 0.6,
    Satisfaction: 0.9,
    Excitement: 0.9,
    Pride: 0.7,
    Triumph: 0.9,
    "Aesthetic Appreciation": 0.7,
    Contemplation: 0.2,
    Interest: 0.5,
    Anxiety: -0.7,
    Distress: -0.7,
    Doubt: -0.4,
    Anger: -0.8,
    Tiredness: -0.3,
    Frustration: -0.5,
  }

  const valence = emotionValence[emotion] || 0
  let baseColor = "bg-gray-500"

  if (valence < 0.2) {
    baseColor = "bg-orange-500"
  } else if (valence >= -0.2 && valence <= 0.3) {
    baseColor = "bg-purple-500"
  } else if (valence > 0.3) {
    baseColor = "bg-green-500"
  }

  const opacity = Math.max(0.3, intensity)
  return `${baseColor} opacity-${Math.round(opacity * 100)}`
}

const getEmotionBadgeColor = (emotion: string) => {
  // Hume emotion valence mapping for color coding
  const emotionValence: { [key: string]: number } = {
    Concentration: 0.3,
    Determination: 0.6,
    Satisfaction: 0.9,
    Excitement: 0.9,
    Pride: 0.7,
    Triumph: 0.9,
    "Aesthetic Appreciation": 0.7,
    Contemplation: 0.2,
    Interest: 0.5,
    Anxiety: -0.7,
    Distress: -0.7,
    Doubt: -0.4,
    Anger: -0.8,
    Tiredness: -0.3,
    Frustration: -0.5,
  }

  const valence = emotionValence[emotion] || 0

  // Color coding based on valence: orange < 0.2, purple -0.2 to 0.3, green > 0.3
  if (valence < 0.2) {
    return "bg-orange-100 text-orange-800 border-orange-200"
  } else if (valence >= -0.2 && valence <= 0.3) {
    return "bg-purple-100 text-purple-800 border-purple-200"
  } else {
    return "bg-green-100 text-green-800 border-green-200"
  }
}

const TrendIcon = ({ trend }: { trend: string }) => {
  switch (trend) {
    case "up":
      return <TrendingUp className="h-4 w-4 text-green-600" />
    case "down":
      return <TrendingDown className="h-4 w-4 text-red-600" />
    case "stable":
      return <Minus className="h-4 w-4 text-gray-600" />
    default:
      return null
  }
}

const getHealthScoreColor = (score: number) => {
  if (score >= 80) return "text-green-600 bg-green-50 border-green-200"
  if (score >= 60) return "text-yellow-600 bg-yellow-50 border-yellow-200"
  return "text-red-600 bg-red-50 border-red-200"
}

const getRiskBadgeColor = (riskLevel: string) => {
  const colors = {
    improving: "bg-green-100 text-green-800",
    "no change": "bg-gray-100 text-gray-800",
    "at-risk": "bg-red-100 text-red-800",
  }
  return colors[riskLevel as keyof typeof colors] || "bg-gray-100 text-gray-800"
}

const getDescriptiveEmotion = (emotion: string) => {
  const emotionMap: { [key: string]: string } = {
    Concentration: "Concentrated",
    Determination: "Determined",
    Satisfaction: "Satisfied",
    Excitement: "Excited",
    Pride: "Proud",
    Triumph: "Triumphant",
    "Aesthetic Appreciation": "Aesthetically Appreciative",
    Contemplation: "Contemplative",
    Interest: "Interested",
    Anxiety: "Anxious",
    Distress: "Distressed",
    Doubt: "Doubtful",
    Anger: "Angry",
    Tiredness: "Tired",
    Frustration: "Frustrated",
  }

  return emotionMap[emotion] || emotion
}

export function OrgOverview({
  selectedOrganization = "entire", // Updated default to match filter value
  selectedTracker = "all", // Updated default to match filter value
}: OrgOverviewProps) {
  const [selectedTeam, setSelectedTeam] = useState("All Teams")
  const [selectedTeamDetail, setSelectedTeamDetail] = useState<TeamOverview | null>(null)
  const [teams, setTeams] = useState<TeamOverview[]>(mockTeams)
  const [loading, setLoading] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState<"day" | "week" | "month">("day")
  const user = useSelector((state: any) => state.auth.user)
  const organization_id = user?.organization_info?._id

  // Fetch team aggregates from API
  useEffect(() => {
    const fetchTeamAggregates = async () => {
      if (!organization_id) {
        console.log("[OrgOverview] No organization_id found, using mock data")
        return
      }

      setLoading(true)
      try {
        const response = await getOrgEqTeamAggregates(organization_id, selectedPeriod)
        console.log("[OrgOverview] Team aggregates response:", response)

        if (response?.success && response?.team_aggregates) {
          // Map API response to TeamOverview interface
          const mappedTeams: TeamOverview[] = response.team_aggregates.map((agg: any) => ({
            id: agg.team_id || agg._id,
            name: agg.team_name,
            memberCount: agg.team_member_count || 0,
            positivityScore: Math.round(agg.positivity_percentage || 0),
            recentEmotions:
              agg.top_emotions?.slice(0, 3).map((e: any) => {
                // Capitalize first letter of emotion
                return e.emotion.charAt(0).toUpperCase() + e.emotion.slice(1)
              }) || [],
            emotionIntensity: agg.top_emotions?.[0]?.percentage / 100 || 0.5,
            trend: agg.trend || "stable",
            riskLevel:
              agg.positivity_percentage >= 70
                ? "improving"
                : agg.positivity_percentage >= 50
                  ? "no change"
                  : "at-risk",
            topTheme: agg.top_tracker?.tracker_name || "General Activity",
            trajectorySummary: agg.trajectory_summary || null,
          }))

          setTeams(mappedTeams)
          console.log("[OrgOverview] Mapped teams:", mappedTeams)
        }
      } catch (error) {
        console.error("[OrgOverview] Failed to fetch team aggregates:", error)
        // Fall back to mock data on error
        setTeams(mockTeams)
      } finally {
        setLoading(false)
      }
    }

    fetchTeamAggregates()
  }, [organization_id, selectedPeriod])

  console.log("[v0] OrgOverview - selectedOrganization:", selectedOrganization)
  console.log("[v0] OrgOverview - selectedTracker:", selectedTracker)
  console.log("[v0] OrgOverview - teams length:", teams.length)

  const filteredTeams =
    !selectedOrganization || selectedOrganization === "entire"
      ? teams
      : teams.filter((team) => team.name.toLowerCase() === selectedOrganization.toLowerCase())

  console.log("[v0] OrgOverview - filteredTeams length:", filteredTeams.length)
  console.log(
    "[v0] OrgOverview - filteredTeams:",
    filteredTeams.map((t) => t.name),
  )

  const highRiskTeams = filteredTeams.filter((team) => team.riskLevel === "at-risk").length
  const totalMembers = filteredTeams.reduce((sum, team) => sum + team.memberCount, 0)

  const avgPositivityScore =
    filteredTeams.length > 0
      ? filteredTeams.reduce((sum, team) => sum + team.positivityScore, 0) / filteredTeams.length
      : 77 // Default fallback score

  console.log("[v0] OrgOverview - totalMembers:", totalMembers)
  console.log("[v0] OrgOverview - avgPositivityScore:", avgPositivityScore)

  const getOrganizationTrend = () => {
    const trendCounts = filteredTeams.reduce(
      (acc, team) => {
        acc[team.trend] = (acc[team.trend] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

    if ((trendCounts.up || 0) > (trendCounts.down || 0)) return "up"
    if ((trendCounts.down || 0) > (trendCounts.up || 0)) return "down"
    return "stable"
  }

  const organizationTrend = getOrganizationTrend()

  const handleTeamClick = (team: TeamOverview) => {
    setSelectedTeamDetail(team)
  }

  const handleBackToOverview = () => {
    setSelectedTeamDetail(null)
  }

  if (selectedTeamDetail) {
    return (
      <TeamDetailPage
        team={selectedTeamDetail}
        organizationId={organization_id}
        period={selectedPeriod}
        onBack={handleBackToOverview}
      />
    )
  }

  const healthScore = Math.round(avgPositivityScore)

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between gap-6 mb-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-full border-2 ${getHealthScoreColor(healthScore)}`}
              >
                <div className="text-center">
                  <div className="text-xl font-bold">{healthScore}</div>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <TrendIcon trend={organizationTrend} />
                <span className="text-xs text-muted-foreground mt-1">Trend</span>
              </div>
            </div>

            <div className="flex-1">
              <CardTitle className="text-xl font-semibold">
                {!selectedOrganization || selectedOrganization === "entire"
                  ? "Organization Overview"
                  : `${selectedOrganization.charAt(0).toUpperCase() + selectedOrganization.slice(1)} Overview`}
              </CardTitle>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{totalMembers} members</span>
                </div>
                {highRiskTeams > 0 && (
                  <div className="flex items-center gap-1 text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>
                      {highRiskTeams} team{highRiskTeams > 1 ? "s" : ""} at risk
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Period Filter */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedPeriod("day")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                selectedPeriod === "day"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Last 24 Hours
            </button>
            <button
              onClick={() => setSelectedPeriod("week")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                selectedPeriod === "week"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Last Week
            </button>
            <button
              onClick={() => setSelectedPeriod("month")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                selectedPeriod === "month"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Last Month
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredTeams.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No teams found for the selected organization filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTeams.map((team) => (
              <Card
                key={team.id}
                className="relative overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleTeamClick(team)}
              >
                <div
                  className={`absolute top-0 left-0 w-full h-1 ${getEmotionColor(team.recentEmotions[0], team.emotionIntensity)}`}
                />
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-base">{team.name}</h3>
                        <Badge variant="outline" className="text-xs font-bold">
                          {team.positivityScore}%
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {team.memberCount} members
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendIcon trend={team.trend} />
                      <Badge variant="outline" className={getRiskBadgeColor(team.riskLevel)}>
                        {team.riskLevel}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground font-medium">Recent Emotional Profile:</div>
                      <div className="flex flex-wrap gap-1">
                        {team.recentEmotions.map((emotion, index) => (
                          <Badge key={index} variant="secondary" className={`text-xs ${getEmotionBadgeColor(emotion)}`}>
                            {getDescriptiveEmotion(emotion)}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="pt-2 border-t border-border">
                      <div className="text-xs text-muted-foreground">
                        Top theme: <span className="font-medium text-foreground">{team.topTheme}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
